import { queryKeys } from '@constants/queryKeys';
import { useRealtimeInvalidate } from '@hooks/useRealtimeInvalidate';
import type { MasterResource } from '@services/master.service';
import { useState } from 'react';

import { useCreateMasterItem, useDeleteMasterItem, useMasterList } from '../useSettingsData';

import { EditableListCard, type ListItem } from './EditableListCard';

const toListItems = (data?: { _id: string; name: string }[]): ListItem[] =>
  (data ?? []).map((d) => ({ id: d._id, name: d.name }));

interface MasterListSectionProps {
  resource: MasterResource;
  title: string;
  placeholder: string;
  /** Singular noun used in the info text, e.g. 'Bank', 'Course', 'location' — exact casing preserved. */
  itemLabel: string;
  /** Text appended after "configured · ", e.g. 'Changes apply immediately to the Dashboard.' */
  contextText: string;
  leadingIcon?: React.ElementType;
  isAdmin: boolean;
}

export const MasterListSection = ({
  resource,
  title,
  placeholder,
  itemLabel,
  contextText,
  leadingIcon,
  isAdmin,
}: MasterListSectionProps) => {
  const { data, isLoading } = useMasterList(resource);
  const createItem = useCreateMasterItem(resource);
  const deleteItem = useDeleteMasterItem(resource);
  const [newValue, setNewValue] = useState('');

  // Live-refresh when another admin adds/renames/deactivates a banks/courses/
  // centres record elsewhere — see the `<resource>:changed` realtime docs.
  useRealtimeInvalidate(resource, [queryKeys.master.list(resource)]);

  const handleAdd = () => {
    const name = newValue.trim();
    if (!name) return;
    createItem.mutate(name, { onSuccess: () => setNewValue('') });
  };

  const count = (data ?? []).length;

  return (
    <EditableListCard
      title={title}
      items={toListItems(data)}
      loading={isLoading}
      addPending={createItem.isPending}
      newValue={newValue}
      placeholder={placeholder}
      infoText={`${count} ${itemLabel}${count !== 1 ? 's' : ''} configured · ${contextText}`}
      leadingIcon={leadingIcon}
      isAdmin={isAdmin}
      onChange={setNewValue}
      onAdd={handleAdd}
      onRemove={(id) => deleteItem.mutate(id)}
    />
  );
};
